import { Section } from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import background from '@/static/assets/vets/vet-hero.svg';
import { Flex, font, preventOneWordInLastLine, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import { useAnalyticsContext } from '@lemonade-hq/nuiza';
import parse from 'html-react-parser';
import type React from 'react';
import styled from 'styled-components';

const MainHeader = styled.h1`
  text-wrap: balance;
  white-space: pre-line;
  ${font('alt', { fontSize: '2.8rem', lineHeight: '3.2rem' })};
  text-align: center;

  ${MEDIA_QUERIES.tabletAndDesktop`
    font-size: 5.5rem;
    line-height: 1.3;
  `}
`;

const MobileTitle = styled(MainHeader)`
  display: block;
  ${MEDIA_QUERIES.tabletAndDesktop`
    display: none;
  `}
`;

const DesktopTitle = styled(MainHeader)`
  display: none;
  ${MEDIA_QUERIES.tabletAndDesktop`
    display: block;
  `}
`;

export const SectionParagraph = styled.p`
  text-align: center;
  white-space: normal;
  margin-bottom: 20px;

  ${font('main', { fontSize: '1.8rem', lineHeight: '2.4rem' })};
  ${MEDIA_QUERIES.tabletAndDesktop`
    font-size: 2rem;
    white-space: pre-line;
    max-width: 440px;
  `}
`;

const MainWrapper = styled(Section)`
  padding: 60px 20px 80px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        padding: 67px 20px 0;
        min-height: 692px;
        width: 100vw;
        background: linear-gradient(to top, ${themedColor('cloud')} 140px, white 140px);
    `}
`;

const ImageContainer = styled(Flex)`
  display: none;

  ${MEDIA_QUERIES.tabletAndDesktop`
    display: block;
    width: 100%;
    height: 0;
    position: absolute;
    top: 70px;
    overflow: visible;
  `}
`;

const BackgroundImage = styled(Flex)`
  display: none;

  ${MEDIA_QUERIES.tabletAndDesktop`
    display: block;
    position: relative;
    background-image: url(${background.src});
    height: 725px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center bottom;
  `};
`;

const SectionWrapper = styled(Flex)`
  flex-direction: column;
  gap: 20px;
  align-items: center;
  ${MEDIA_QUERIES.mobile`
        max-width: 334px;
    `}
`;

export interface HeroProps {
  title: string;
  titleMobile?: string;
  subtitle: string;
  ctaText: string;
  ctaUrl: string;
}

export const Hero: React.FC<HeroProps> = ({ title, titleMobile, subtitle, ctaText, ctaUrl }) => {
  const { trackEvent } = useAnalyticsContext();

  const handleCtaClick = () => {
    trackEvent('vet_landing_page_button_click');
  };

  return (
    <MainWrapper flexDirection="column">
      <ImageContainer>
        <BackgroundImage flex="1" />
      </ImageContainer>
      <SectionWrapper>
        {titleMobile ? (
          <>
            <MobileTitle>{titleMobile}</MobileTitle>
            <DesktopTitle>{title}</DesktopTitle>
          </>
        ) : (
          <MainHeader>{title}</MainHeader>
        )}
        <SectionParagraph>{parse(preventOneWordInLastLine(subtitle))}</SectionParagraph>

        <JoinCTA absoluteUrl={ctaUrl} onClick={handleCtaClick} flavor="primary" minWidth={160} glowing>
          {ctaText}
        </JoinCTA>
      </SectionWrapper>
    </MainWrapper>
  );
};
